import { useEffect, useState } from 'react';
import { teamOverridesApi } from '../utils/api';
import { Users, Plus, Trash2, Save, X, AlertCircle, CheckCircle2, Loader, Info, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

/**
 * Excepciones de equipo (Team Overrides)
 *
 * Permite añadir manualmente miembros extra al equipo de un responsable
 * (más allá de los reportes directos cargados desde el CSV de RRHH).
 * Útil para casos como dredondo@valorizasm.com que necesita ver
 * a ciertos N+2 como si fueran directos.
 */
export default function TeamOverridesAdmin() {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null); // existing override being edited, or null for new
  const [managerEmail, setManagerEmail] = useState('');
  const [memberInput, setMemberInput] = useState('');
  const [memberEmails, setMemberEmails] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await teamOverridesApi.list();
      setOverrides(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error cargando las excepciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setManagerEmail('');
    setMemberInput('');
    setMemberEmails([]);
    setError('');
    setOpenDialog(true);
  };

  const openEdit = (ov) => {
    setEditing(ov);
    setManagerEmail(ov.manager?.email || '');
    setMemberInput('');
    setMemberEmails((ov.extra_members || []).map(m => m.email));
    setError('');
    setOpenDialog(true);
  };

  const addEmailFromInput = () => {
    const raw = memberInput.trim().toLowerCase();
    if (!raw) return;
    // Allow paste of comma/space/newline separated emails
    const parts = raw.split(/[\s,;]+/).filter(Boolean);
    const next = Array.from(new Set([...memberEmails, ...parts]));
    setMemberEmails(next);
    setMemberInput('');
  };

  const removeEmail = (em) => {
    setMemberEmails(memberEmails.filter(e => e !== em));
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!managerEmail.trim()) {
      setError('Indica el email del responsable');
      return;
    }
    setSaving(true);
    try {
      // Flush any pending email still in the input
      let emails = memberEmails;
      if (memberInput.trim()) {
        const extras = memberInput.trim().toLowerCase().split(/[\s,;]+/).filter(Boolean);
        emails = Array.from(new Set([...emails, ...extras]));
      }
      await teamOverridesApi.upsert(managerEmail.trim().toLowerCase(), emails);
      setSuccess(editing ? 'Excepción actualizada' : 'Excepción creada');
      setOpenDialog(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error guardando la excepción');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (managerEmailToDelete) => {
    if (!window.confirm(`¿Eliminar la excepción de ${managerEmailToDelete}?`)) return;
    setError('');
    try {
      await teamOverridesApi.remove(managerEmailToDelete);
      setSuccess('Excepción eliminada');
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error eliminando la excepción');
    }
  };

  return (
    <div className="space-y-6" data-testid="team-overrides-admin">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <Users className="h-6 w-6 text-brand" />
            Excepciones de equipo
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Añade miembros extra al equipo de un responsable, además de los reportes
            directos definidos en RRHH. Útil cuando un manager debe gestionar a personas
            que no son sus subordinados directos.
          </p>
        </div>
        <Button onClick={openNew} data-testid="btn-new-override" className="gap-2">
          <Plus className="h-4 w-4" /> Nueva excepción
        </Button>
      </div>

      {/* Info box */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 dark:border-blue-900/40 dark:bg-blue-900/10 p-4 flex gap-3 text-sm">
        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-blue-900 dark:text-blue-200 space-y-1">
          <p className="font-medium">¿Cómo funciona?</p>
          <ul className="list-disc list-inside space-y-0.5 text-blue-800 dark:text-blue-300">
            <li>El responsable verá a estos miembros en sus aprobaciones, dashboard y calendario semanal.</li>
            <li>Las excepciones <strong>persisten entre sincronizaciones</strong> del CSV diario.</li>
            <li>Si un email ya es subordinado directo, se ignora (no se duplica).</li>
          </ul>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2" data-testid="override-error">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900/30 dark:bg-green-900/10 p-3 text-sm text-green-700 dark:text-green-300 flex items-start gap-2" data-testid="override-success">
          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader className="h-5 w-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : overrides.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="overrides-empty">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>No hay excepciones configuradas.</p>
          <p className="mt-1">Pulsa “Nueva excepción” para añadir la primera.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {overrides.map((ov) => (
            <div
              key={ov.manager?.id}
              className="rounded-xl border border-border bg-card p-4 shadow-sm"
              data-testid={`override-row-${ov.manager?.email}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">
                      {ov.manager?.first_name} {ov.manager?.last_name}
                    </span>
                    <span className="text-xs text-muted-foreground">{ov.manager?.email}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(ov.extra_members || []).length === 0 ? (
                      <span className="text-xs italic text-muted-foreground">Sin miembros extra</span>
                    ) : (
                      (ov.extra_members || []).map((m) => (
                        <span
                          key={m.id}
                          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
                            m.is_active
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300'
                              : 'bg-muted border-border text-muted-foreground line-through'
                          }`}
                          title={m.email}
                        >
                          {m.first_name} {m.last_name}
                        </span>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Actualizado {ov.updated_at ? new Date(ov.updated_at).toLocaleString('es-ES') : '—'}
                    {ov.updated_by && <> · por <span className="font-medium">{ov.updated_by}</span></>}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(ov)}
                    data-testid={`btn-edit-${ov.manager?.email}`}
                  >
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={() => handleDelete(ov.manager?.email)}
                    data-testid={`btn-delete-${ov.manager?.email}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-lg" data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Editar excepción' : 'Nueva excepción'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div>
              <Label htmlFor="manager-email">Email del responsable</Label>
              <Input
                id="manager-email"
                placeholder="dredondo@valorizasm.com"
                value={managerEmail}
                disabled={!!editing}
                onChange={(e) => setManagerEmail(e.target.value)}
                data-testid="input-manager-email"
                className="mt-1"
              />
              {editing && (
                <p className="text-xs text-muted-foreground mt-1">
                  Para cambiar de responsable, borra esta excepción y crea otra.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="member-input">Miembros extra (emails)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="member-input"
                  placeholder="email@valorizasm.com (puedes pegar varios separados por coma)"
                  value={memberInput}
                  onChange={(e) => setMemberInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addEmailFromInput();
                    }
                  }}
                  data-testid="input-member-email"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addEmailFromInput}
                  data-testid="btn-add-member-email"
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
              </div>

              {memberEmails.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3" data-testid="member-emails-list">
                  {memberEmails.map((em) => (
                    <span
                      key={em}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted border border-border"
                    >
                      {em}
                      <button
                        type="button"
                        onClick={() => removeEmail(em)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`btn-remove-${em}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Dejar vacío y guardar es equivalente a eliminar la excepción.
              </p>
            </div>

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-2.5 text-xs text-destructive flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenDialog(false)} disabled={saving} data-testid="btn-cancel-override">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-override" className="gap-2">
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
